import React, { useState } from 'react';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { shadows } from '../theme/shadows';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    children: React.ReactNode;
    isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    disabled = false,
    onClick,
    children,
    style,
    isLoading = false,
    ...props
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    // Capture button width before loading to prevent layout shift
    React.useEffect(() => {
        if (isLoading && buttonRef.current && !buttonWidth) {
            setButtonWidth(buttonRef.current.offsetWidth);
        }
    }, [isLoading, buttonWidth]);

    // Base styles shared by all variants
    const baseStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        fontWeight: typography.weights.medium,
        // Refinement: Increased vertical padding for larger presence (0.5rem -> 0.75rem)
        padding: `${spacing[12]} ${spacing[24]}`,
        // Refinement: Softer, modern border radius (6px -> 8px)
        borderRadius: '8px',
        border: 'none',
        cursor: isLoading ? 'default' : (disabled ? 'not-allowed' : 'pointer'),
        transition: 'transform 150ms ease-out, box-shadow 150ms ease-out, opacity 150ms ease-out, background-color 150ms ease-out, border-color 150ms ease-out',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 'none',
        // Refinement: Clearer disabled state
        opacity: disabled ? 0.5 : 1,
        ...style,
    };

    // Variant specific styles
    const getVariantStyles = (): React.CSSProperties => {
        switch (variant) {
            case 'primary':
                return {
                    backgroundColor: colors.primary.navy,
                    color: '#FFFFFF',
                    fontWeight: typography.weights.semiBold, // Stronger presence
                    // Larger padding for primary CTA
                    padding: `${spacing[16]} ${spacing[32]}`,
                    boxShadow: disabled ? 'none' : '0 2px 8px -2px rgba(0, 0, 0, 0.15)',
                };
            case 'secondary':
                return {
                    backgroundColor: 'transparent',
                    color: colors.text.muted, // Reduced from navy to muted
                    border: `1px solid ${colors.text.muted}`,
                    boxShadow: 'none',
                    fontWeight: typography.weights.regular, // Lighter weight
                };
            case 'ghost':
                return {
                    backgroundColor: isHovered && !disabled ? colors.background.offWhite : 'transparent',
                    color: colors.text.primary,
                    boxShadow: 'none',
                };
            default:
                return {};
        }
    };

    // Hover overrides
    const getHoverStyles = (): React.CSSProperties => {
        if (disabled) return {};
        if (!isHovered) return {};

        switch (variant) {
            case 'primary':
                return {
                    backgroundColor: colors.primary.navy,
                    boxShadow: isActive ? '0 2px 8px -2px rgba(0, 0, 0, 0.15)' : '0 4px 12px -2px rgba(0, 0, 0, 0.2)',
                    transform: isActive ? 'translateY(0)' : 'translateY(-1px)',
                    transition: isActive ? 'transform 80ms ease-out, box-shadow 80ms ease-out' : undefined,
                };
            case 'secondary':
                return {
                    backgroundColor: 'rgba(107, 114, 128, 0.04)', // Very subtle tint matching muted
                    color: colors.primary.navy, // Strengthen on hover
                    borderColor: colors.primary.navy,
                };
            case 'ghost':
                return {};
            default:
                return {};
        }
    };

    // Focus styles for accessibility
    const getFocusStyles = (): React.CSSProperties => {
        if (!isFocused) return {};
        return {
            boxShadow: `0 0 0 2px ${colors.background.offWhite}, 0 0 0 4px ${colors.primary.navy}`,
        }
    }

    const combinedStyles = {
        ...baseStyle,
        ...getVariantStyles(),
        ...getHoverStyles(),
        ...getFocusStyles(),
    };

    // Spinner component
    const Spinner = () => (
        <>
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block',
                }}
            >
                <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="24 12"
                />
            </svg>
        </>
    );

    return (
        <button
            ref={buttonRef}
            onClick={onClick}
            disabled={disabled || isLoading}
            onMouseEnter={() => !isLoading && setIsHovered(true)}
            onMouseLeave={() => { setIsHovered(false); setIsActive(false); }}
            onMouseDown={() => !disabled && !isLoading && setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={combinedStyles}
            {...props}
        >
            <span style={{
                opacity: isLoading ? 0 : 1,
                transition: 'opacity 120ms ease-out',
                display: isLoading ? 'none' : 'inline',
            }}>
                {children}
            </span>
            {isLoading && (
                <span style={{
                    opacity: 1,
                    transition: 'opacity 120ms ease-out',
                }}>
                    <Spinner />
                </span>
            )}
        </button>
    );
};
